import React, { useEffect, useState } from "react";
import { EventBus } from "../../engine/EventBus.js";
import type { AbilityName, AbilityScores } from "../../engine/types/entity.js";
import {
  ABILITY_CAP,
  applyAsi,
  validateAsiChoice,
  type AsiChoice,
} from "../../engine/party/asi.js";

/**
 * ASI modal (W10).
 *
 * Subscribes to `world:level_up` and queues every event where
 * `asiRequired` is true. Each queued level-up opens the modal with the
 * character's current ability scores; confirming writes the patched
 * scores back via `onConfirm` and pops the next queue entry. The
 * modal blocks input until every queued ASI is resolved (design:
 * "Can't be deferred").
 */

export interface AsiCharacterSummary {
  readonly instanceId: string;
  readonly name: string;
  readonly abilities: AbilityScores;
}

interface QueueItem {
  readonly instanceId: string;
  readonly name: string;
  readonly newLevel: number;
}

interface Props {
  readonly getCharacter: (instanceId: string) => AsiCharacterSummary | null;
  readonly onConfirm: (instanceId: string, abilities: AbilityScores) => void;
}

const ABILITIES: readonly AbilityName[] = ["STR", "DEX", "CON", "INT", "WIS", "CHA"];

export function AsiModal({ getCharacter, onConfirm }: Props) {
  const [queue, setQueue] = useState<readonly QueueItem[]>([]);
  const [choice, setChoice] = useState<AsiChoice>({ kind: "one", ability: "STR" });

  useEffect(() => {
    return EventBus.on("world:level_up", (e) => {
      if (!e.asiRequired) return;
      setQueue((q) => [...q, { instanceId: e.instanceId, name: e.name, newLevel: e.newLevel }]);
    });
  }, []);

  const head = queue[0];
  const character = head ? getCharacter(head.instanceId) : null;
  if (!head || !character) return null;

  const error = validateAsiChoice(choice);
  const preview = applyAsi(character.abilities, choice).abilities;

  function confirm() {
    if (!head || !character) return;
    if (error !== null) return;
    const next = applyAsi(character.abilities, choice).abilities;
    onConfirm(head.instanceId, next);
    setQueue((q) => q.slice(1));
    setChoice({ kind: "one", ability: "STR" });
  }

  return (
    <div
      data-testid="asi-modal"
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(0,0,0,0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 300,
        pointerEvents: "auto",
      }}
    >
      <div
        style={{
          background: "#1a1a1a",
          border: "1px solid rgba(255,255,255,0.3)",
          borderRadius: 8,
          padding: "24px 32px",
          minWidth: 360,
          color: "white",
          fontFamily: "monospace",
        }}
      >
        <h2 style={{ margin: 0, fontSize: 18, letterSpacing: 2 }}>
          {character.name.toUpperCase()} — LEVEL {head.newLevel}
        </h2>
        <div style={{ color: "#aaa", fontSize: 12, marginTop: 4 }}>
          Ability Score Improvement — choose +2 to one, or +1 to two different.
        </div>

        <div style={{ marginTop: 16 }}>
          <label style={{ marginRight: 12 }}>
            <input
              type="radio"
              checked={choice.kind === "one"}
              onChange={() => setChoice({ kind: "one", ability: "STR" })}
            />
            {" "}+2 to one
          </label>
          <label>
            <input
              type="radio"
              checked={choice.kind === "two"}
              onChange={() => setChoice({ kind: "two", first: "STR", second: "DEX" })}
            />
            {" "}+1 to two
          </label>
        </div>

        <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8 }}>
          {choice.kind === "one" ? (
            <AbilitySelect
              label="Ability"
              value={choice.ability}
              onChange={(a) => setChoice({ kind: "one", ability: a })}
            />
          ) : (
            <>
              <AbilitySelect
                label="First"
                value={choice.first}
                onChange={(a) => setChoice({ ...choice, first: a })}
              />
              <AbilitySelect
                label="Second"
                value={choice.second}
                onChange={(a) => setChoice({ ...choice, second: a })}
              />
            </>
          )}
        </div>

        <table style={{ marginTop: 16, fontSize: 12, width: "100%" }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", color: "#888" }}>Stat</th>
              <th style={{ textAlign: "right", color: "#888" }}>Now</th>
              <th style={{ textAlign: "right", color: "#888" }}>After</th>
            </tr>
          </thead>
          <tbody>
            {ABILITIES.map((a) => (
              <tr key={a}>
                <td>{a}</td>
                <td style={{ textAlign: "right" }}>{character.abilities[a]}</td>
                <td
                  style={{
                    textAlign: "right",
                    color:
                      preview[a] === ABILITY_CAP && preview[a] !== character.abilities[a]
                        ? "#fc6"
                        : preview[a] !== character.abilities[a]
                          ? "#6c6"
                          : "white",
                  }}
                >
                  {preview[a]}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {error && (
          <div style={{ color: "#c66", fontSize: 12, marginTop: 8 }}>{error}</div>
        )}

        <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end" }}>
          <button
            type="button"
            disabled={error !== null}
            onClick={confirm}
            style={{
              background: error ? "#333" : "#2a6",
              color: "white",
              border: "1px solid rgba(255,255,255,0.3)",
              borderRadius: 4,
              padding: "8px 20px",
              fontFamily: "inherit",
              cursor: error ? "default" : "pointer",
            }}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

function AbilitySelect({
  label,
  value,
  onChange,
}: {
  readonly label: string;
  readonly value: AbilityName;
  readonly onChange: (a: AbilityName) => void;
}) {
  return (
    <label style={{ fontSize: 12 }}>
      <span style={{ color: "#aaa", marginRight: 6 }}>{label}:</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as AbilityName)}
        style={{
          background: "#222",
          color: "white",
          border: "1px solid rgba(255,255,255,0.2)",
          padding: "3px 6px",
          fontFamily: "monospace",
        }}
      >
        {ABILITIES.map((a) => (
          <option key={a} value={a}>
            {a}
          </option>
        ))}
      </select>
    </label>
  );
}
